import { useCallback } from 'react';
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { TextField, useFormField } from '@frontend/design-system';
import { PhoneHoursForm, usePhoneHoursForm } from '../../../phone-hours/phone-hours-form';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';

export const PhoneHoursStep = ({
  initialState,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS>) => {
  const { t } = useTranslation('phone');

  const nameFieldProps = useFormField({
    required: true,
    type: 'text',
    value: initialState?.callObject?.primitiveName,
  });

  const { formProps, getPhoneHours } = usePhoneHoursForm({
    phoneHours: initialState?.callObject?.phoneHoursExpansion?.phoneHours ?? [],
    keepNewIds: true,
  });

  const emptyNameField = nameFieldProps.value === '';
  const normalizePhoneHours = (phoneHours: any[]) =>
    phoneHours.slice().sort((a, b) => a.scheduleId.localeCompare(b.scheduleId));
  const hasEmptyBreakName = Object.values(formProps.breakSchedules).some(
    (breakSchedule) => breakSchedule.name.trim() === ''
  );

  const hasChanges = () => {
    const currentPhoneHours = getPhoneHours();
    const initialPhoneHours = initialState?.callObject?.phoneHoursExpansion?.phoneHours ?? [];

    const nameChanged = nameFieldProps.value !== initialState?.callObject?.primitiveName;

    const phoneHoursChanged = !isEqual(normalizePhoneHours(currentPhoneHours), normalizePhoneHours(initialPhoneHours));

    return nameChanged || phoneHoursChanged;
  };

  const handleProceedClick = useCallback(() => {
    const phoneHours = getPhoneHours();

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS,
      callObject: {
        primitiveId: initialState?.callObject.primitiveId ?? '',
        primitiveName: nameFieldProps.value,
        instructionId: initialState?.callObject.instructionId ?? '',
        instructionSetId: initialState?.callObject.instructionSetId ?? '',
        phoneHoursExpansion: {
          phoneHours,
        },
      },
    });
  }, [
    getPhoneHours,
    onProceedClick,
    initialState?.callObject.primitiveId,
    nameFieldProps.value,
    initialState?.callObject.instructionId,
    initialState?.callObject.instructionSetId,
  ]);

  return (
    <Step
      title={t('Phone Hours')}
      description={t('Define Open, Closed, and Break hours in order to route calls differently by time of day.')}
      primaryButtonDisabled={!hasChanges() || emptyNameField || hasEmptyBreakName}
      onPrimaryButtonClick={handleProceedClick}
    >
      <TextField
        {...nameFieldProps}
        name='phoneHours'
        label={t('Phone Hours Name')}
        error={t('Phone Hours Name is a required field.')}
        autoFocus={!initialState?.callObject.primitiveName && !nameFieldProps.value}
        css={css`
          width: 318px;
        `}
      />

      <PhoneHoursForm {...formProps} routingActionsEnabled />
    </Step>
  );
};
